import type { Workflow, KbVersion, ApiError, WorkflowFormData, Tenant, TenantFormData, AdminTicket, AdminTicketThread, TicketCounts, TicketStatus, ReplyAttachment } from '@/types';

// In dev, Vite proxies /api/* to the PHP backend.
// In production, set VITE_API_BASE in .env to the absolute API URL.
const API_BASE = import.meta.env.VITE_API_BASE || '';

class ApiClient {
  private token: string | null = null;

  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('admin_token', token);
  }

  loadToken(): void {
    this.token = localStorage.getItem('admin_token');
  }

  clearToken(): void {
    this.token = null;
    localStorage.removeItem('admin_token');
  }

  hasToken(): boolean {
    return Boolean(this.token);
  }

  getToken(): string | null {
    return this.token;
  }

  isSuperAdmin(): boolean {
    if (!this.token) return false;
    try {
      const b64 = this.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(b64));
      const perms: unknown[] = Array.isArray(payload.permissions) ? payload.permissions : [];
      return perms.includes('*') || perms.includes('super.admin');
    } catch {
      return false;
    }
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, { ...options, headers });

    if (!response.ok) {
      let error: ApiError;
      try {
        error = await response.json();
      } catch {
        error = { error: 'unknown', message: `HTTP ${response.status}` };
      }
      throw new ApiClientError(error.message, error.error, response.status);
    }

    if (response.status === 204) return undefined as T;
    return response.json();
  }

  // ─── Gitea OAuth ───────────────────────────────────────────

  async giteaAuthUrl(): Promise<{ url: string; state: string }> {
    return this.request('/api/admin/auth/gitea/url');
  }

  async giteaCallback(code: string): Promise<{
    token: string;
    user: { name: string; email: string; login: string };
  }> {
    return this.request('/api/admin/auth/gitea/callback', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  // ─── Health ────────────────────────────────────────────────

  async health(): Promise<{
    app: string;
    database: string;
    kb: string;
    php_version?: string;
    timestamp?: string;
  }> {
    return this.request('/api/support/health');
  }

  // ─── Workflows (admin) ─────────────────────────────────────

  async listWorkflows(status?: string): Promise<Workflow[]> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const res = await this.request<{ workflows: Workflow[] }>(`/api/admin/workflows${qs}`);
    return res.workflows;
  }

  async getWorkflow(id: number): Promise<Workflow> {
    const res = await this.request<{ workflow: Workflow }>(`/api/admin/workflows/${id}`);
    return res.workflow;
  }

  async createWorkflow(data: WorkflowFormData): Promise<Workflow> {
    const res = await this.request<{ workflow: Workflow }>('/api/admin/workflows', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.workflow;
  }

  async updateWorkflow(id: number, data: Partial<WorkflowFormData>): Promise<Workflow> {
    const res = await this.request<{ workflow: Workflow }>(`/api/admin/workflows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.workflow;
  }

  async deleteWorkflow(id: number): Promise<Workflow> {
    const res = await this.request<{ workflow: Workflow }>(`/api/admin/workflows/${id}`, {
      method: 'DELETE',
    });
    return res.workflow;
  }

  // ─── KB publish / versions ─────────────────────────────────

  async publishKb(notes?: string): Promise<{
    version: number;
    path: string;
    workflow_count: number;
    json_url: string;
  }> {
    const res = await this.request<{
      published: { version: number; path: string; workflow_count: number; json_url: string };
    }>('/api/admin/kb/publish', {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
    return res.published;
  }

  async listVersions(): Promise<KbVersion[]> {
    const res = await this.request<{ versions: KbVersion[] }>('/api/admin/kb/versions');
    return res.versions;
  }

  // ─── Test panel — preview agent responses against draft+published KB ──

  async testKb(params: {
    message: string;
    role: string;
    statuses?: string[];
    history?: Array<{ role: 'user' | 'assistant'; content: string }>;
  }): Promise<{
    text: string;
    action: { type: string; url: string } | null;
    media: import('@/types').MediaItem[];
    meta: {
      tokens_used: number;
      latency_ms: number;
      filtered_workflow_count: number;
      total_workflow_count: number;
      permissions_used: string[];
    };
  }> {
    return this.request('/api/admin/kb/test', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // ─── Tenants ───────────────────────────────────────────────

  async listTenants(): Promise<Tenant[]> {
    const res = await this.request<{ tenants: Tenant[] }>('/api/admin/tenants');
    return res.tenants;
  }

  async getTenant(id: string): Promise<Tenant> {
    const res = await this.request<{ tenant: Tenant }>(`/api/admin/tenants/${id}`);
    return res.tenant;
  }

  async createTenant(data: TenantFormData): Promise<Tenant> {
    const res = await this.request<{ tenant: Tenant }>('/api/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.tenant;
  }

  async updateTenant(id: string, data: Partial<Omit<Tenant, 'id' | 'tenant_id' | 'created_at'>>): Promise<Tenant> {
    const res = await this.request<{ tenant: Tenant }>(`/api/admin/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.tenant;
  }

  async deleteTenant(id: string): Promise<void> {
    await this.request(`/api/admin/tenants/${id}`, { method: 'DELETE' });
  }

  async updateTenantUser(tenantId: string, userId: number, status: 'active' | 'suspended'): Promise<void> {
    await this.request(`/api/admin/tenants/${tenantId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // ─── Admin Tickets ─────────────────────────────────────────

  async listAdminTickets(status?: TicketStatus): Promise<{ tickets: AdminTicket[]; counts: TicketCounts }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request(`/api/admin/tickets${qs}`);
  }

  async getAdminTicket(id: number): Promise<{ ticket: AdminTicket; threads: AdminTicketThread[] }> {
    return this.request(`/api/admin/tickets/${id}`);
  }

  async adminReplyTicket(
    id: number,
    message: string,
    attachments?: ReplyAttachment[],
  ): Promise<{ reply_id: number; created_at: string }> {
    return this.request(`/api/admin/tickets/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify({ message, attachments: attachments ?? [] }),
    });
  }

  async setTicketStatus(id: number, status: TicketStatus): Promise<{ status: TicketStatus }> {
    return this.request(`/api/admin/tickets/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // ─── File uploads ──────────────────────────────────────────

  async uploadFile(file: File): Promise<{
    url: string;
    type: 'image' | 'video';
    size: number;
    mime: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    // Don't set Content-Type — the browser fills in the boundary.
    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const response = await fetch(`${API_BASE}/api/admin/uploads`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      let error: ApiError;
      try {
        error = await response.json();
      } catch {
        error = { error: 'unknown', message: `HTTP ${response.status}` };
      }
      throw new ApiClientError(error.message, error.error, response.status);
    }
    return response.json();
  }
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export const api = new ApiClient();
api.loadToken();
